/**
 * Grading for the QParam programming exercises. Every check reads the output of FormalRV's own tools
 * (the in-browser builds): whether the reader accepts a program, the census the compiler prints in
 * the .qppm, the text of the compiled stages, and the resource VM's verdict. Nothing here decides
 * what a program means; that is Lean's job, and an exercise says so when it matters.
 *
 * Plain TypeScript with erasable syntax only, so formalrv-web/check_exercises.ts can run it in Node.
 */

export type CensusField = 'meas' | 'frame' | 'magicT' | 'synthEps'

export type Check =
  | { kind: 'accepted'; label: string }
  | { kind: 'lowered'; label: string }
  | { kind: 'census'; label: string; unit?: string; field: CensusField; expect: string; at: Record<string, number>[]; tables?: Record<string, number[]> }
  | { kind: 'text'; label: string; stage: 'prog' | 'qppm' | 'qec' | 'sysprog'; pattern: string; count?: number; absent?: boolean }
  | { kind: 'vm'; label: string; status: string; code?: string }
  | { kind: 'machine'; label: string; field: string; equals: number; others?: 'unchanged' }
  | { kind: 'contract'; label: string; field: string; equals: number }

/** A real FormalRV file (or an excerpt of one) a review exercise shows, as it is at a pinned commit. */
export interface Artifact {
  title: string
  path: string
  commit: string
  language: 'qparam' | 'lean4' | 'json' | 'plaintext'
  text: string
  /** where the excerpt starts in the file (1-based), when it is not the whole file */
  firstLine?: number
  /** how the text shown differs from the file, when it does (an omitted line, selected fields) */
  note?: string
}

/** A question about the artifacts; the answer is computed from them when the exercises are built. */
export interface Question {
  q: string
  type: 'number' | 'text' | 'choice'
  answer: string | number
  accept?: string[]
  choices?: string[]
  /** relative tolerance for numbers (0 = exact) */
  tolerance?: number
  explain: string
}

export interface Exercise {
  id: string
  unit: string
  title: string
  chapter: string
  goal: string[]
  kind: 'prog' | 'sysprog' | 'review'
  artifacts?: Artifact[]
  questions?: Question[]
  /** for a VM exercise: the explorer example whose source the listing was compiled from */
  context?: string
  starter: string
  solution: string
  /** the parameter point prepare_qec specialises at (prog exercises) */
  point?: Record<string, number>
  checks: Check[]
  hints: string[]
}

export interface Outputs {
  prog?: string
  accepted: boolean
  acceptLog: string
  lowered: boolean | null
  lowerLog: string
  qppm?: string
  qec?: string
  sysprog?: string
  vm?: Record<string, unknown> | null
  starterSysprog?: string
}

export interface CheckResult { label: string; pass: boolean; detail: string }

// ---------------------------------------------------------------------------------------------
// The census the compiler prints: `spec NAME:` … `resource { meas: E, frame: E, magicT: E, … }`
// ---------------------------------------------------------------------------------------------

export function censusOf(qppm: string): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {}
  let current: string | null = null
  for (const line of qppm.split('\n')) {
    const spec = line.match(/^spec\s+([A-Za-z_][\w']*)\s*:/)
    if (spec) { current = spec[1]; continue }
    const res = line.match(/^\s*resource\s*\{(.*)\}/)
    if (res && current && !out[current]) {
      const fields: Record<string, string> = {}
      for (const part of res[1].split(/,(?![^(\[]*[)\]])/)) {
        const [k, ...v] = part.split(':')
        if (k && v.length) fields[k.trim()] = v.join(':').trim()
      }
      out[current] = fields
    }
  }
  return out
}

/** The program's own census row: `spec program` for an unnamed program, else the named one. */
export function programUnit(qppm: string): string {
  const named = qppm.match(/^program\s+([A-Za-z_][\w']*)\s*\[/m)
  return named ? named[1] : 'program'
}

// ---------------------------------------------------------------------------------------------
// Natural-number arithmetic of QParam bodies: truncated subtraction, floor division.
// ---------------------------------------------------------------------------------------------

type Tok = { t: 'num'; v: number } | { t: 'id'; v: string } | { t: 'op'; v: string }

function tokenize(src: string): Tok[] {
  const s = src.replace(/·/g, '*').replace(/×/g, '*')
  const toks: Tok[] = []
  let i = 0
  while (i < s.length) {
    const c = s[i]
    if (/\s/.test(c)) { i++; continue }
    if (/\d/.test(c)) { let j = i; while (j < s.length && /\d/.test(s[j])) j++; toks.push({ t: 'num', v: Number(s.slice(i, j)) }); i = j; continue }
    if (/[A-Za-z_]/.test(c)) { let j = i; while (j < s.length && /[\w']/.test(s[j])) j++; toks.push({ t: 'id', v: s.slice(i, j) }); i = j; continue }
    if ('+-*/%()^[]<.Σ'.includes(c)) { toks.push({ t: 'op', v: c }); i++; continue }
    throw new Error(`cannot read '${c}' in ${src}`)
  }
  return toks
}

/** Evaluate a census formula: natural arithmetic, table lookups `tSynth[k+1]` (tables from the
 *  check), and sums `Σ v < e . body` as the compiler prints them for loops. */
export function evalNat(src: string, env: Record<string, number>, tables: Record<string, number[]> = {}): number {
  const toks = tokenize(src)
  let p = 0
  const peek = () => toks[p]
  const take = () => toks[p++]
  const expr = (): number => {
    let v = term()
    while (peek()?.t === 'op' && (peek().v === '+' || peek().v === '-')) {
      const op = take().v; const r = term()
      v = op === '+' ? v + r : Math.max(0, v - r)
    }
    return v
  }
  const term = (): number => {
    let v = power()
    while (peek()?.t === 'op' && '*/%'.includes(String(peek().v))) {
      const op = take().v; const r = power()
      v = op === '*' ? v * r : op === '/' ? (r === 0 ? 0 : Math.floor(v / r)) : (r === 0 ? v : v % r)
    }
    return v
  }
  const power = (): number => {
    const b = atom()
    if (peek()?.t === 'op' && peek().v === '^') { take(); return b ** power() }
    return b
  }
  const atom = (): number => {
    const tk = take()
    if (!tk) throw new Error(`unexpected end of ${src}`)
    if (tk.t === 'num') return tk.v
    if (tk.t === 'id') {
      if (peek()?.t === 'op' && peek().v === '[') {
        take(); const k = expr()
        if (take()?.v !== ']') throw new Error(`missing ] in ${src}`)
        const table = tables[tk.v]
        if (!table) throw new Error(`no table ${tk.v} for ${src}`)
        return table[k] ?? 0
      }
      if (!(tk.v in env)) throw new Error(`unknown name ${tk.v} in ${src}`)
      return env[tk.v]
    }
    if (tk.v === 'Σ') {
      // Σ v < bound . body: the body runs to the closing parenthesis, as the compiler prints it
      const v = take()
      if (v?.t !== 'id' || take()?.v !== '<') throw new Error(`cannot read the sum in ${src}`)
      const bound = expr()
      if (take()?.v !== '.') throw new Error(`cannot read the sum in ${src}`)
      const start = p
      let total = 0
      const saved = env[v.v]
      for (let i = 0; i < bound; i++) { p = start; env[v.v] = i; total += expr() }
      if (bound === 0) { env[v.v] = 0; expr() }
      if (saved === undefined) delete env[v.v]; else env[v.v] = saved
      return total
    }
    if (tk.v === '(') { const v = expr(); if (take()?.v !== ')') throw new Error(`missing ) in ${src}`); return v }
    throw new Error(`unexpected ${tk.v} in ${src}`)
  }
  const v = expr()
  if (p !== toks.length) throw new Error(`trailing input in ${src}`)
  return v
}

// ---------------------------------------------------------------------------------------------
// Machine lines and contracts of a .sysprog
// ---------------------------------------------------------------------------------------------

export function machineOf(sysprog: string): Record<string, number> {
  const m = sysprog.match(/^machine\s*\{(.*)\}/m)
  const out: Record<string, number> = {}
  if (!m) return out
  for (const part of m[1].split(',')) {
    const f = part.match(/^\s*([a-z_0-9]+)\s*:\s*(\d+)/)
    if (f) out[f[1]] = Number(f[2])
  }
  return out
}

export function contractOf(sysprog: string, unit = 'program'): Record<string, number> {
  const re = new RegExp(`^spec\\s+${unit}\\s*:\\s*\\n\\s*resource\\s*\\{(.*)\\}`, 'm')
  const m = sysprog.match(re)
  const out: Record<string, number> = {}
  if (!m) return out
  for (const part of m[1].split(',')) {
    const f = part.match(/^\s*([a-z_0-9]+)\s*:\s*(\d+)/)
    if (f) out[f[1]] = Number(f[2])
  }
  return out
}

// ---------------------------------------------------------------------------------------------

export function grade(ex: Exercise, o: Outputs): CheckResult[] {
  return ex.checks.map(c => {
    try {
      switch (c.kind) {
        case 'accepted':
          return { label: c.label, pass: o.accepted, detail: o.accepted ? 'prepare_qppm accepts the program' : lastLine(o.acceptLog) }
        case 'lowered':
          return { label: c.label, pass: o.lowered === true, detail: o.lowered ? 'prepare_qec lowers it to the code' : (o.lowered === null ? 'not reached' : lastLine(o.lowerLog)) }
        case 'census': {
          if (!o.qppm) return { label: c.label, pass: false, detail: 'no .qppm: the program did not compile' }
          const census = censusOf(o.qppm)
          const unit = c.unit ?? programUnit(o.qppm)
          const got = census[unit]?.[c.field]
          if (got === undefined) return { label: c.label, pass: false, detail: `no census for ${unit} in the .qppm` }
          for (const env of c.at) {
            const want = evalNat(c.expect, { ...env }, c.tables), have = evalNat(got, { ...env }, c.tables)
            if (want !== have) {
              const at = Object.entries(env).map(([k, v]) => `${k} = ${v}`).join(', ')
              return { label: c.label, pass: false, detail: `the compiler counts ${c.field}: ${got}, which is ${have} at ${at}; expected ${want}` }
            }
          }
          return { label: c.label, pass: true, detail: `the compiler counts ${c.field}: ${got}` }
        }
        case 'text': {
          const text = o[c.stage]
          if (!text) return { label: c.label, pass: false, detail: `no .${c.stage} produced` }
          const n = (text.match(new RegExp(c.pattern, 'gm')) ?? []).length
          const pass = c.absent ? n === 0 : c.count !== undefined ? n === c.count : n > 0
          const what = c.absent ? `found ${n}, expected none` : c.count !== undefined ? `found ${n}, expected ${c.count}` : (n ? `found ${n}` : 'not found')
          return { label: c.label, pass, detail: `.${c.stage}: ${what}` }
        }
        case 'vm': {
          const v = o.vm
          if (!v) return { label: c.label, pass: false, detail: 'the VM has not checked this listing yet' }
          const pass = v.status === c.status && (!c.code || v.code === c.code)
          return { label: c.label, pass, detail: `${v.status}${v.code ? ` ${v.code}` : ''}${v.message ? `: ${v.message}` : ''}` }
        }
        case 'machine': {
          if (!o.sysprog) return { label: c.label, pass: false, detail: 'no listing' }
          const m = machineOf(o.sysprog)
          if (m[c.field] !== c.equals) return { label: c.label, pass: false, detail: `${c.field} is ${m[c.field] ?? 'missing'}` }
          if (c.others === 'unchanged' && o.starterSysprog) {
            const before = machineOf(o.starterSysprog)
            const changed = Object.keys({ ...before, ...m }).filter(k => k !== c.field && before[k] !== m[k])
            if (changed.length) return { label: c.label, pass: false, detail: `also changed: ${changed.join(', ')}` }
          }
          return { label: c.label, pass: true, detail: `${c.field}: ${c.equals}` }
        }
        case 'contract': {
          if (!o.sysprog) return { label: c.label, pass: false, detail: 'no listing' }
          const k = contractOf(o.sysprog)
          return { label: c.label, pass: k[c.field] === c.equals, detail: `spec program ${c.field}: ${k[c.field] ?? 'missing'}` }
        }
        default:
          return { label: (c as { label: string }).label, pass: false, detail: 'unknown check' }
      }
    } catch (e) {
      return { label: c.label, pass: false, detail: (e as Error).message }
    }
  })
}

// ---------------------------------------------------------------------------------------------
// Review questions
// ---------------------------------------------------------------------------------------------

const normText = (s: string) => s.toLowerCase().replace(/[`\s]+/g, '').replace(/[.。]$/, '')

export function gradeAnswer(q: Question, input: string): boolean {
  const raw = input.trim()
  if (!raw) return false
  if (q.type === 'number') {
    const v = Number(raw.replace(/[,_\s]/g, ''))
    const want = Number(q.answer)
    if (!Number.isFinite(v)) return false
    const tol = q.tolerance ?? 0
    return tol === 0 ? v === want : Math.abs(v - want) <= tol * Math.abs(want)
  }
  if (q.type === 'choice') return raw === String(q.answer)
  return [String(q.answer), ...(q.accept ?? [])].some(a => normText(a) === normText(raw))
}

function lastLine(log: string): string {
  const lines = log.split('\n').map(l => l.trim()).filter(Boolean)
  return lines.find(l => /refused|error/i.test(l)) ?? lines[lines.length - 1] ?? 'refused'
}
